import React, { useEffect, useState } from 'react';
import { Modal } from 'semantic-ui-react';
import { X } from 'react-feather';
import { MARKET_SCAN_NEW_ROUTE } from 'constants/routes';
import { INSURANCE_TYPES_NAMES } from 'constants/insurance-types-names';
import { MARKET_SCAN_AVERAGE_SAVINGS } from 'constants/marketScan';
import { TRACKING_EVENTS } from 'helpers/segment';
import { trackEvent } from 'helpers';
import { IPolicy } from 'types';
import { Link } from 'react-router-dom';
import carIcon from 'assets/icons/car.svg';
import homeAndContentsIcon from 'assets/icons/home-and-contents.svg';
import styles from './PartnerPurchaseModal.module.scss';

const QuashedIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    width="56"
    height="56"
    viewBox="0 0 56 56"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M37.8732 42.948C42.5941 38.7732 45.5708 32.6708 45.5708 25.8733C45.5708 13.2893 35.3694 3.08791 22.7853 3.08791C10.2014 3.08791 0 13.2893 0 25.8733C0 35.8111 6.36211 44.263 15.2355 47.3782C14.3587 47.9727 13.2547 49.0388 13.8619 50.557C14.3446 51.764 15.7097 51.2639 17.9571 50.4405C22.4658 48.7888 30.5255 45.8361 42.1363 52.7557C43.5325 53.5878 45.3714 53.2723 46.2965 51.9359C47.2482 50.5612 46.8664 48.6709 45.4559 47.7733L37.8732 42.948ZM22.7853 39.4183C15.3046 39.4183 9.2403 33.3541 9.2403 25.8733C9.2403 18.3925 15.3046 12.3282 22.7853 12.3282C30.2661 12.3282 36.3305 18.3925 36.3305 25.8733C36.3305 33.3541 30.2661 39.4183 22.7853 39.4183Z"
      fill="#339BFA"
    />
  </svg>
);

interface IProps {
  open?: boolean;
  policy: IPolicy;
  onClose?: () => void;
}

const modalDimmer = { className: styles.dimmer };

const PartnerPurchaseModal = ({ open: showModal, policy, onClose }: IProps) => {
  const [open, setOpen] = useState(showModal);

  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  useEffect(() => {
    setOpen(showModal);
  }, [showModal]);

  const getMarketScanRoutes = () => ({
    vehicleRoute: MARKET_SCAN_NEW_ROUTE.replace(
      ':policyTypeName?',
      INSURANCE_TYPES_NAMES.VEHICLE,
    ),
    homeRoute: MARKET_SCAN_NEW_ROUTE.replace(
      ':policyTypeName?',
      INSURANCE_TYPES_NAMES.HOME_CONTENTS,
    ),
  });

  const routes = getMarketScanRoutes();

  const handleVehicleClick = () => {
    trackEvent(TRACKING_EVENTS.PolicyDetailsPartnerModalVehicleClick, {
      policyType: policy.policyType,
      policyProvider: policy.policyProviderName,
    });
  };

  const handleHomeClick = () => {
    trackEvent(TRACKING_EVENTS.PolicyDetailsPartnerModalHomeClick, {
      policyType: policy.policyType,
      policyProvider: policy.policyProviderName,
    });
  };

  return (
    open && (
      <Modal
        open={open}
        className={styles.modal}
        onClose={handleClose}
        size="small"
        closeOnDimmerClick={true}
        closeIcon={false}
        dimmer={modalDimmer}
      >
        <Modal.Content className={styles.content}>
          <button
            type="button"
            className={styles.close}
            onClick={handleClose}
            aria-label="Close"
          >
            <X className={styles.closeIcon} />
          </button>
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <div className={styles.heading}>
                <span className={styles.headingRow}>
                  <QuashedIcon className={styles.logo} />
                  <span>
                    We've loved helping you to<br />shop & save on insurance!
                  </span>
                </span>
              </div>
            </div>
          </div>
          <div className={styles.policyCardContainer}>
            <div className={styles.policyCard}>
              <div className={styles.titleContainer}>
                <img src={carIcon} alt="Vehicle" className={styles.buttonIcon} />
                <div>
                  <div className={styles.policyType}>
                    <span className={styles.fullText}>Shop another Vehicle policy</span>
                    <span className={styles.shortText}>Shop Vehicle</span>
                  </div>
                  <div className={styles.avgSaving}>
                    <div className={styles.price}>
                      Average savings {MARKET_SCAN_AVERAGE_SAVINGS[INSURANCE_TYPES_NAMES.VEHICLE]}
                    </div>
                  </div>
                </div>
              </div>
              <Link
                to={routes.vehicleRoute}
                className={styles.cardButton}
                onClick={handleVehicleClick}
              >
                <span>Compare</span>
              </Link>
            </div>
            <div className={styles.policyCard}>
              <div className={styles.titleContainer}>
                <img src={homeAndContentsIcon} alt="House & Contents" className={styles.buttonIcon} />
                <div>
                  <div className={styles.policyType}>
                    <span className={styles.fullText}>Shop House & Contents policy</span>
                    <span className={styles.shortText}>House & Contents</span>
                  </div>
                  <div className={styles.avgSaving}>
                    <div className={styles.price}>
                      Average savings {MARKET_SCAN_AVERAGE_SAVINGS[INSURANCE_TYPES_NAMES.HOME_CONTENTS]}
                    </div>
                  </div>
                </div>
              </div>
              <Link
                to={routes.homeRoute}
                className={styles.cardButton}
                onClick={handleHomeClick}
              >
                <span>Compare</span>
              </Link>
            </div>
          </div>
        </Modal.Content>
      </Modal>
    )
  );
};

export default React.memo(PartnerPurchaseModal);
